import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { Icon } from '../Icon.js';

/**
 * Which layout the width allows:
 * - `three`: at least 1280px — navigation, the source and the decisions side by side;
 * - `drawer`: 900–1279px — the source and the decisions, the navigation behind a button;
 * - `stacked`: under 900px — one pane at a time, chosen with a switch.
 */
export type PaneLayout = 'three' | 'drawer' | 'stacked';
export type Pane = 'left' | 'center' | 'right';

const WIDE = '(min-width: 1280px)';
const MEDIUM = '(min-width: 900px)';

function layoutNow(): PaneLayout {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'three';
  if (window.matchMedia(WIDE).matches) return 'three';

  return window.matchMedia(MEDIUM).matches ? 'drawer' : 'stacked';
}

/** The layout for the window as it is now, following it as it is resized. */
export function usePaneLayout(): PaneLayout {
  const [layout, setLayout] = useState<PaneLayout>(layoutNow);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const queries = [window.matchMedia(WIDE), window.matchMedia(MEDIUM)];
    const update = () => {
      setLayout(layoutNow());
    };
    for (const query of queries) query.addEventListener?.('change', update);

    return () => {
      for (const query of queries) query.removeEventListener?.('change', update);
    };
  }, []);

  return layout;
}

/**
 * THREE PANES, EACH SCROLLING ON ITS OWN: navigation, the source of truth, the decisions.
 *
 * On a desk the three sit side by side and fill the window below the page's header, so working
 * through scene 60 of 93 never scrolls the screenplay away from its breakdown. Narrower, the
 * navigation moves into a drawer behind a button; on a phone the three take turns behind a
 * switch. Nothing ever scrolls the page sideways.
 *
 * Which pane shows, whether the drawer is open, whether the navigation is folded and whether the
 * source has the whole width (focus) are the caller's state, so the caller can keep them where it
 * keeps the rest of its view.
 */
export function ThreePaneWorkspace({
  layout,
  left,
  center,
  right,
  labels,
  leftCollapsed = false,
  focus = false,
  stackedPane,
  onStackedPane,
  drawerOpen,
  onDrawerOpen,
  drawerButtonLabel,
  testId,
}: {
  layout: PaneLayout;
  left: ReactNode;
  center: ReactNode;
  right: ReactNode;
  /** Each pane's name, said by the landmark and by the phone's switch. */
  labels: Readonly<Record<Pane, string>>;
  /** The navigation folded to a narrow strip (three columns only). */
  leftCollapsed?: boolean;
  /** The source alone, full width (three columns and the drawer layout). */
  focus?: boolean;
  stackedPane: Pane;
  onStackedPane: (pane: Pane) => void;
  drawerOpen: boolean;
  onDrawerOpen: (open: boolean) => void;
  /** What the drawer button says, beside the navigation's name. */
  drawerButtonLabel?: string;
  testId: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);

  // Fill the window below whatever sits above the workspace, re-measured when the window changes.
  useLayoutEffect(() => {
    const node = root.current;
    if (node === null || layout === 'stacked') return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setTop(Math.max(0, Math.round(rect.top + window.scrollY)));
    };
    measure();
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [layout]);

  // Escape closes the drawer, as it closes every overlay in the workspace.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen, onDrawerOpen]);

  const height = layout === 'stacked' ? undefined : `max(560px, calc(100dvh - ${String(top + 20)}px))`;
  const showLeft = !focus && (layout === 'three' || (layout === 'stacked' && stackedPane === 'left'));
  const showRight = !focus && (layout !== 'stacked' || stackedPane === 'right');
  const showCenter = layout !== 'stacked' || stackedPane === 'center';

  return (
    <div
      ref={root}
      className="ui-three"
      data-layout={layout}
      data-focus={focus ? '1' : '0'}
      data-collapsed={leftCollapsed ? '1' : '0'}
      data-testid={testId}
      style={{ height }}
    >
      {layout === 'stacked' ? (
        <div className="ui-three-switch" role="group" aria-label="Show" data-testid={`${testId}-switch`}>
          {(['left', 'center', 'right'] as const).map((pane) => (
            <button
              key={pane}
              type="button"
              className="ui-filter"
              aria-pressed={stackedPane === pane}
              data-testid={`${testId}-switch-${pane}`}
              onClick={() => {
                onStackedPane(pane);
              }}
            >
              {labels[pane]}
            </button>
          ))}
        </div>
      ) : null}

      {layout === 'drawer' && !focus ? (
        <div className="ui-three-bar">
          <button
            type="button"
            className="ctl ui-three-drawer-button"
            aria-expanded={drawerOpen}
            data-testid={`${testId}-drawer-open`}
            onClick={() => {
              onDrawerOpen(!drawerOpen);
            }}
          >
            <Icon name="panelLeft" size={15} />
            {labels.left}
            {drawerButtonLabel === undefined ? null : <span className="ui-three-drawer-current">{drawerButtonLabel}</span>}
          </button>
        </div>
      ) : null}

      <div className="ui-three-grid">
        {showLeft ? (
          <nav className="ui-three-pane ui-three-left" aria-label={labels.left} data-testid={`${testId}-left`}>
            {left}
          </nav>
        ) : null}
        {showCenter ? (
          <section className="ui-three-pane ui-three-center" aria-label={labels.center} data-testid={`${testId}-center`}>
            {center}
          </section>
        ) : null}
        {showRight ? (
          <section className="ui-three-pane ui-three-right" aria-label={labels.right} data-testid={`${testId}-right`}>
            {right}
          </section>
        ) : null}

        {layout === 'drawer' && drawerOpen && !focus ? (
          <>
            <div
              className="ui-three-scrim"
              aria-hidden="true"
              onClick={() => {
                onDrawerOpen(false);
              }}
            />
            <nav className="ui-three-pane ui-three-drawer" aria-label={labels.left} data-testid={`${testId}-drawer`}>
              {left}
            </nav>
          </>
        ) : null}
      </div>
    </div>
  );
}
